import { useLocation } from "react-router-dom";

// Determine language + path helpers from the current route.
// Russian pages live under the /ru prefix, English at the root.
export function useLang() {
  const { pathname } = useLocation();
  const lang = pathname === "/ru" || pathname.startsWith("/ru/") ? "ru" : "en";
  const prefix = lang === "ru" ? "/ru" : "";
  // Build a localized path, e.g. lp('/profile') -> '/ru/profile' (ru) or '/profile' (en)
  const lp = (path) => {
    if (path === "/") return prefix || "/";
    return `${prefix}${path}`;
  };
  return { lang, prefix, lp, t: translations[lang] };
}

// Given the current pathname, return the equivalent path in the other language.
export function toggledPath(pathname) {
  if (pathname === "/ru" || pathname.startsWith("/ru/")) {
    const stripped = pathname.replace(/^\/ru/, "");
    return stripped || "/";
  }
  return `/ru${pathname === "/" ? "" : pathname}`;
}

export const translations = {
  en: {
    branding: "Method by Lana Ivanova",
    common: {
      back: "Back",
      home: "Home",
      results: "Results",
      history: "History",
      downloadPDF: "Download PDF",
      saving: "Saving...",
      submit: "Submit",
    },
    landing: {
      brand: "Speaking Wheel",
      heroLine1: "Your Wheel of",
      heroHighlight: "Public Speaking",
      heroSubtitle: "Assess 8 speaking skills. Get your personalized 5-week development program.",
      startAssessment: "Start Assessment",
      seeSample: "See Sample Report",
      areasTitle: "8 Areas of Speaking Excellence",
      areasSubtitle: "Discover your strengths and growth opportunities across all dimensions of effective public speaking.",
      howItWorks: "How It Works",
      steps: [
        { title: "Take the Assessment", desc: "Answer questions across 8 speaking areas. Takes about 10 minutes." },
        { title: "See Your Results", desc: "Get your personalized wheel showing strengths and growth areas." },
        { title: "Follow Your Plan", desc: "Receive a 5-week program with daily practices and weekly challenges." },
      ],
      wheelTitle: "Your Personal Speaking Wheel",
      wheelSubtitle: "See exactly where you stand across all 8 areas. Here's an example of what your results will look like.",
      sampleResult: "Sample assessment result",
      sampleOverall: "Sample Overall Score",
      viewFullSample: "View Full Sample Report",
      ctaTitle: "Ready to Level Up Your Speaking?",
      ctaSubtitle: "Join thousands of professionals who have transformed their public speaking skills.",
      startYourAssessment: "Start Your Assessment",
      footerCopy: "© 2026 Your Wheel of Public Speaking",
    },
    profile: {
      title: "Tell Us About Yourself",
      subtitle: "This helps us personalize your development program.",
      nameLabel: "Name *",
      namePlaceholder: "Your name",
      emailLabel: "Email *",
      emailPlaceholder: "your@email.com",
      consent: "By continuing, you agree to receive emails from Lana Ivanova",
      contextLabel: "Speaking Context",
      contextPlaceholder: "Where do you speak most often?",
      goalLabel: "Main Goal",
      goalPlaceholder: "What do you want to achieve as a speaker?",
      continueBtn: "Continue to Assessment",
      errEnterName: "Please enter your name",
      errEnterEmail: "Please enter your email",
      saved: "Profile saved! Let's start your assessment.",
      genericError: "Something went wrong. Please try again.",
    },
    assessment: {
      stepOf: (n) => `Step ${n} of 8`,
      disagree: "1 - Strongly disagree",
      agree: "10 - Strongly agree",
      importanceQuestion: (area) => `How important is improving ${area} for your next 30 days?`,
      notImportant: "Not important",
      veryImportant: "Very important",
      notesLabel: "Notes / Examples (optional)",
      notesPlaceholder: (area) => `Any specific challenges or goals related to ${area}?`,
      currentScore: (area) => `Current ${area} Score`,
      complete: "Complete Assessment",
      next: (area) => `Next: ${area}`,
      errAnswerAll: "Please answer all questions before continuing",
      errLoad: "Failed to load assessment data",
      done: "Assessment complete!",
      errSave: "Failed to save assessment. Please try again.",
    },
    results: {
      title: "Your Speaking Wheel Results",
      resultsFor: (name) => `Results for ${name}`,
      sampleReport: "This is a sample report",
      overallScore: "Overall Score",
      focusAreas: "Your 5 Focus Areas",
      allAreas: "All Areas",
      focusAreaBadge: "Focus Area",
      viewProgram: "View Your 5-Week Program",
      discussTitle: "Want to discuss your plan?",
      discussSubtitle: "Book a free 30-minute session with Lana to review your program and explore how we can work together.",
      bookSession: "Book a Free 30-Minute Session",
      notFound: "Assessment not found",
      errLoad: "Failed to load results",
      generatingPDF: "Generating PDF...",
      pdfDone: "PDF downloaded!",
      pdfFail: "Failed to generate PDF",
      calModalTitle: "Calendar link is not available yet",
      calModalDesc: "Please leave your email and we'll contact you to schedule a session.",
      contactThanks: "Thank you! We'll be in touch soon.",
      errEnterEmail: "Please enter your email",
      pdf: {
        title: "Your Wheel of Public Speaking",
        name: "Name",
        date: "Date",
        overall: "Overall Score",
        areaScores: "Area Scores",
        focusAreas: "Your 5 Focus Areas",
        score: "Score",
        fileName: "speaking-wheel-results.pdf",
      },
    },
    program: {
      titlePre: "Your",
      titleHighlight: "5-Week",
      titlePost: "Program",
      subtitle: "Each week focuses on one priority area with specific actions, drills, and real-life applications.",
      scoreLabel: (s) => `Score: ${s}/10`,
      supporting: "Supporting",
      whyItMatters: "Why It Matters",
      whatToPractice: "What to Practice",
      twoMinDrill: "2-Minute Drill",
      weeklyChallenge: "Weekly Challenge",
      supportingFocus: (name) => `Supporting Focus: ${name}`,
      realLife: "Real-Life Application",
      notFound: "Program not found",
      errLoad: "Failed to load program",
      calUnavailable: "Calendar link is not available yet",
      discussTitle: "Want to discuss your plan?",
      discussSubtitle: "Book a free 30-minute session with Lana to review your program and explore how we can work together.",
      bookSession: "Book a Free 30-Minute Session",
      generatingPDF: "Generating PDF...",
      pdfDone: "PDF downloaded!",
      pdfFail: "Failed to generate PDF",
      pdf: {
        title: "5-Week Public Speaking Program",
        name: "Name",
        date: "Date",
        week: "Week",
        why: "Why it matters",
        actions: "Actions",
        drill: "2-Minute Drill",
        challenge: "Weekly Challenge",
        fileName: "5-week-speaking-program.pdf",
      },
    },
    history: {
      retake: "Retake Assessment",
      title: "Your Assessment History",
      subtitle: (name) => `Track your progress over time.${name ? ` Welcome back, ${name}!` : ""}`,
      noneTitle: "No assessments yet",
      noneDesc: "Complete your first assessment to start tracking your progress.",
      startAssessment: "Start Assessment",
      latestVsPrevious: "Latest vs Previous",
      overallProgress: "Overall Progress",
      percentageChange: "Percentage Change",
      areasImproved: "Areas Improved",
      ofAreas: "of 8 areas",
      areaProgress: "Area Progress",
      allAssessments: "All Assessments",
      latest: "Latest",
      score: (s, p) => `Score: ${s}/80 (${p}%)`,
      errLoad: "Failed to load history",
    },
  },
  ru: {
    branding: "Метод Ланы Ивановой",
    common: {
      back: "Назад",
      home: "На главную",
      results: "Результаты",
      history: "История",
      downloadPDF: "Скачать PDF",
      saving: "Сохранение...",
      submit: "Отправить",
    },
    landing: {
      brand: "Колесо Выступлений",
      heroLine1: "Твое Колесо",
      heroHighlight: "Публичных Выступлений",
      heroSubtitle: "Оцените 8 навыков выступления. Получите персональную 5-недельную программу развития.",
      startAssessment: "Начать оценку",
      seeSample: "Посмотреть пример отчета",
      areasTitle: "8 областей мастерства выступлений",
      areasSubtitle: "Откройте свои сильные стороны и зоны роста по всем измерениям эффективных публичных выступлений.",
      howItWorks: "Как это работает",
      steps: [
        { title: "Пройдите оценку", desc: "Ответьте на вопросы по 8 областям выступлений. Займет около 10 минут." },
        { title: "Смотрите результаты", desc: "Получите персональное колесо с сильными сторонами и зонами роста." },
        { title: "Следуйте плану", desc: "Получите 5-недельную программу с ежедневными практиками и недельными вызовами." },
      ],
      wheelTitle: "Ваше персональное Колесо Выступлений",
      wheelSubtitle: "Точно узнайте, где вы находитесь по всем 8 областям. Вот пример того, как будут выглядеть ваши результаты.",
      sampleResult: "Пример результата оценки",
      sampleOverall: "Пример общего результата",
      viewFullSample: "Посмотреть полный пример отчета",
      ctaTitle: "Готовы прокачать свои выступления?",
      ctaSubtitle: "Присоединяйтесь к тысячам профессионалов, которые преобразили свои навыки публичных выступлений.",
      startYourAssessment: "Начать оценку",
      footerCopy: "© 2026 Твое Колесо Публичных Выступлений",
    },
    profile: {
      title: "Расскажите о себе",
      subtitle: "Это поможет персонализировать вашу программу развития.",
      nameLabel: "Имя *",
      namePlaceholder: "Ваше имя",
      emailLabel: "Email *",
      emailPlaceholder: "your@email.com",
      consent: "Продолжая, вы соглашаетесь получать письма от Ланы Ивановой",
      contextLabel: "Контекст выступлений",
      contextPlaceholder: "Где вы выступаете чаще всего?",
      goalLabel: "Главная цель",
      goalPlaceholder: "Чего вы хотите достичь как спикер?",
      continueBtn: "Перейти к оценке",
      errEnterName: "Пожалуйста, введите имя",
      errEnterEmail: "Пожалуйста, введите email",
      saved: "Профиль сохранен! Начнем вашу оценку.",
      genericError: "Что-то пошло не так. Попробуйте еще раз.",
    },
    assessment: {
      stepOf: (n) => `Шаг ${n} из 8`,
      disagree: "1 — Полностью не согласен",
      agree: "10 — Полностью согласен",
      importanceQuestion: (area) => `Насколько важно улучшить «${area}» в ближайшие 30 дней?`,
      notImportant: "Не важно",
      veryImportant: "Очень важно",
      notesLabel: "Заметки / примеры (необязательно)",
      notesPlaceholder: (area) => `Есть особые сложности или цели, связанные с «${area}»?`,
      currentScore: (area) => `Текущая оценка «${area}»`,
      complete: "Завершить оценку",
      next: (area) => `Далее: ${area}`,
      errAnswerAll: "Пожалуйста, ответьте на все вопросы, прежде чем продолжить",
      errLoad: "Не удалось загрузить данные оценки",
      done: "Оценка завершена!",
      errSave: "Не удалось сохранить оценку. Попробуйте еще раз.",
    },
    results: {
      title: "Результаты вашего Колеса Выступлений",
      resultsFor: (name) => `Результаты для ${name}`,
      sampleReport: "Это пример отчета",
      overallScore: "Общий результат",
      focusAreas: "Ваши 5 приоритетных областей",
      allAreas: "Все области",
      focusAreaBadge: "Приоритет",
      viewProgram: "Смотреть вашу 5-недельную программу",
      discussTitle: "Хотите обсудить ваш план?",
      discussSubtitle: "Запишитесь на бесплатную 30-минутную сессию с Ланой, чтобы разобрать вашу программу и обсудить сотрудничество.",
      bookSession: "Записаться на бесплатную 30-минутную сессию",
      notFound: "Оценка не найдена",
      errLoad: "Не удалось загрузить результаты",
      generatingPDF: "Генерация PDF...",
      pdfDone: "PDF скачан!",
      pdfFail: "Не удалось создать PDF",
      calModalTitle: "Ссылка на календарь пока недоступна",
      calModalDesc: "Оставьте email, и мы свяжемся с вами для записи на сессию.",
      contactThanks: "Спасибо! Мы скоро свяжемся с вами.",
      errEnterEmail: "Пожалуйста, введите email",
      pdf: {
        title: "Твое Колесо Публичных Выступлений",
        name: "Имя",
        date: "Дата",
        overall: "Общий результат",
        areaScores: "Оценки по областям",
        focusAreas: "Ваши 5 приоритетных областей",
        score: "Оценка",
        fileName: "koleso-vystuplenij-rezultaty.pdf",
      },
    },
    program: {
      titlePre: "Ваша",
      titleHighlight: "5-недельная",
      titlePost: "программа",
      subtitle: "Каждая неделя посвящена одной приоритетной области с конкретными действиями, упражнениями и применением в жизни.",
      scoreLabel: (s) => `Оценка: ${s}/10`,
      supporting: "Поддержка",
      whyItMatters: "Почему это важно",
      whatToPractice: "Что отрабатывать",
      twoMinDrill: "2-минутное упражнение",
      weeklyChallenge: "Вызов недели",
      supportingFocus: (name) => `Дополнительный фокус: ${name}`,
      realLife: "Применение в жизни",
      notFound: "Программа не найдена",
      errLoad: "Не удалось загрузить программу",
      calUnavailable: "Ссылка на календарь пока недоступна",
      discussTitle: "Хотите обсудить ваш план?",
      discussSubtitle: "Запишитесь на бесплатную 30-минутную сессию с Ланой, чтобы разобрать вашу программу и обсудить сотрудничество.",
      bookSession: "Записаться на бесплатную 30-минутную сессию",
      generatingPDF: "Генерация PDF...",
      pdfDone: "PDF скачан!",
      pdfFail: "Не удалось создать PDF",
      pdf: {
        title: "5-недельная программа публичных выступлений",
        name: "Имя",
        date: "Дата",
        week: "Неделя",
        why: "Почему это важно",
        actions: "Действия",
        drill: "2-минутное упражнение",
        challenge: "Вызов недели",
        fileName: "5-nedelnaya-programma.pdf",
      },
    },
    history: {
      retake: "Пройти оценку заново",
      title: "История ваших оценок",
      subtitle: (name) => `Отслеживайте свой прогресс во времени.${name ? ` С возвращением, ${name}!` : ""}`,
      noneTitle: "Пока нет оценок",
      noneDesc: "Пройдите первую оценку, чтобы начать отслеживать прогресс.",
      startAssessment: "Начать оценку",
      latestVsPrevious: "Последняя и предыдущая",
      overallProgress: "Общий прогресс",
      percentageChange: "Изменение в процентах",
      areasImproved: "Областей улучшено",
      ofAreas: "из 8 областей",
      areaProgress: "Прогресс по областям",
      allAssessments: "Все оценки",
      latest: "Последняя",
      score: (s, p) => `Результат: ${s}/80 (${p}%)`,
      errLoad: "Не удалось загрузить историю",
    },
  },
};
